import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../App";
import { Mail, User, Send, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Messaggio inviato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="contact-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-tc-muted hover:text-tc-primary mb-8" data-testid="back-home">
        <ArrowLeft className="w-4 h-4" /> Torna alla home
      </Link>

      <div className="grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-3">Scrivici</div>
          <h1 className="font-serif text-5xl md:text-6xl text-tc-primary leading-[1.05] mb-6">
            Parliamone<br/><em className="text-tc-secondary italic">insieme</em>
          </h1>
          <p className="text-tc-muted leading-relaxed">
            Domande, proposte, collaborazioni, storie da raccontare, realtà da aggiungere alla mappa.
            Scrivi in libertà — leggiamo tutto.
          </p>
          <a href="mailto:info@terracomune.org" className="mt-8 inline-flex items-center gap-2 text-tc-primary hover:text-tc-secondary transition-colors" data-testid="contact-email-direct">
            <Mail className="w-4 h-4" /> info@terracomune.org
          </a>
        </div>

        <div className="md:col-span-7">
          {sent ? (
            <div className="bg-tc-surface rounded-3xl p-10 border border-tc/40 text-center" data-testid="contact-sent">
              <Check className="w-10 h-10 text-tc-accent mx-auto mb-4" />
              <p className="font-serif text-3xl text-tc-primary italic mb-4">Ti abbiamo sentita.</p>
              <p className="text-tc-muted mb-6">Ti risponderemo a <strong className="text-tc-primary">{form.email}</strong> appena possibile.</p>
              <button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }} className="btn-outline text-sm" data-testid="contact-send-another">
                Scrivi un altro messaggio
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-tc-surface rounded-3xl p-8 border border-tc/40 space-y-5" data-testid="contact-form">
              <Field icon={<User className="w-4 h-4" />} label="Il tuo nome" required
                value={form.name} onChange={v => setForm({ ...form, name: v })} testid="contact-name" />
              <Field icon={<Mail className="w-4 h-4" />} label="La tua email" type="email" required
                value={form.email} onChange={v => setForm({ ...form, email: v })} testid="contact-email" />
              <Field label="Oggetto (opzionale)"
                value={form.subject} onChange={v => setForm({ ...form, subject: v })} testid="contact-subject" />
              <div>
                <label className="text-xs uppercase tracking-widest text-tc-muted mb-2 block">Messaggio</label>
                <textarea required rows={6} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder="Racconta cosa ti porta a scrivere…"
                  className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary resize-none" data-testid="contact-message" />
              </div>
              <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="contact-submit">
                <Send className="w-4 h-4" /> {busy ? "Invio…" : "Invia messaggio"}
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}

function Field({ icon, label, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-tc-muted mb-2 block">{label}</label>
      <div className="flex items-center gap-3 border-b border-tc focus-within:border-tc-primary py-2">
        {icon && <span className="text-tc-muted">{icon}</span>}
        <input type={type} value={value} required={required} onChange={e => onChange(e.target.value)}
          className="flex-1 bg-transparent outline-none text-sm" data-testid={testid} />
      </div>
    </div>
  );
}
